/**
 * FIELD-001C · Effective-resource-type resolver.
 *
 * Codex-verified defect: James Carter shows "General / Unspecified"
 * on Workload (where `eng.resourceType || eng.trade || eng.role`
 * yielded the canonical resourceType) but "Installer" on Availability
 * (where a different fallback preferred `role`). Searching "General"
 * excluded him but "Installer" included him — the two callers used
 * two different resolution chains.
 *
 * A single shared resolver removes the divergence WITHOUT any data
 * migration:
 *
 *   1. canonical `resourceType` if present (post-C1B rows)
 *   2. legacy `trade` if present (pre-C1B rows)
 *   3. legacy employment `role` if present (very old rows)
 *   4. otherwise the safe "General / Unspecified" umbrella label
 *
 * Every Ops surface (workload row, availability card, resource
 * summary modal, search filter, assignee picker) MUST call this
 * helper so display and search always agree.
 */
export const GENERAL_RESOURCE_TYPE = "General / Unspecified";

export function effectiveResourceType(eng) {
    if (!eng) return GENERAL_RESOURCE_TYPE;
    const rt = (eng.resourceType || "").trim();
    if (rt) return rt;
    const trade = (eng.trade || "").trim();
    if (trade) return trade;
    const role = (eng.role || "").trim();
    if (role) return role;
    return GENERAL_RESOURCE_TYPE;
}

/**
 * Search-bag builder: joins the user-searchable identity surface for
 * a resource into a single lowercased string. FIELD-001C UAT
 * correction: matches ONLY the effective type (the label the user
 * sees on the row) + name / organisation / email / mobile. Superseded
 * legacy `role`/`trade`/`resourceType` values are NOT added
 * separately — searching "Installer" must not surprise-match a row
 * whose displayed type is "General / Unspecified" just because a
 * legacy `role: "Installer"` still lives on the record.
 */
export function resourceSearchBag(eng) {
    if (!eng) return "";
    return [
        eng.displayName,
        eng.organisationName,
        effectiveResourceType(eng),
        eng.email,
        eng.mobile,
    ].filter(Boolean).join(" ").toLowerCase();
}
